'use client';

/**
 * GlassTabs Component
 *
 * Unified tabs component with glassmorphism styling and built-in security.
 * Supports permission, tier, and license-based tab locking.
 * Automatically adapts to light/dark theme.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Tab, { type TabProps } from '@mui/material/Tab';
import Tabs, { type TabsProps } from '@mui/material/Tabs';
import type { LockConfig, LockState } from '@ops-matrix/shared-types';
import type React from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getTierLabel, usePermissionOptional } from '../../providers/PermissionProvider';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { transientOptions } from '../../utils/styledUtils';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface GlassTabItem {
  /** Display label — accepts plain strings or any ReactNode (e.g. label + badge chip). */
  label: React.ReactNode;

  /** Tab value */
  value: string | number;

  /** Optional icon */
  icon?: React.ReactElement;

  /** Whether tab is disabled (independent of lock state) */
  disabled?: boolean;

  // ---- Security props (optional - makes tabs secure when provided) ----

  /** Single permission required to access this tab */
  permission?: string;

  /** Any of these permissions grants access (OR logic) */
  anyPermission?: string[];

  /** All of these permissions required (AND logic) */
  allPermissions?: string[];

  /** Minimum subscription tier required */
  requiredTier?: LockConfig['requiredTier'];

  /** Specific license type required */
  requiredLicense?: string;

  /** Any of these licenses grants access (OR logic) */
  anyLicense?: string[];
}

export interface GlassTabsProps extends Omit<TabsProps, 'children'> {
  /**
   * Tab items with optional security locking
   */
  tabs: GlassTabItem[];

  /**
   * Enable glassmorphism background
   * @default true
   */
  glass?: boolean;

  /**
   * Glow color for the indicator
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Tab size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Enable pill-shaped tabs (rounded background fill on hover/active).
   * Defaults to `false` — the standard PageTabBar look uses a clean
   * underline indicator and a baseline rule, with no hover background.
   * Set to `true` for filter-bar / segmented-control style usages.
   * @default false
   */
  pill?: boolean;

  /**
   * Whether to show lock icons on locked tabs
   * @default true
   */
  showLockIcon?: boolean;

  /**
   * Whether to show tier badge when tier-locked
   * @default true
   */
  showTierBadge?: boolean;

  /**
   * Custom handler when a locked tab is clicked
   * If not provided, default behavior from PermissionProvider is used
   */
  onLockedTabClick?: (tab: GlassTabItem, lockState: LockState) => void;
}

export interface GlassTabProps extends TabProps {
  /**
   * Custom size
   */
  $size?: 'small' | 'medium' | 'large';

  /**
   * Whether dark mode is active
   */
  $isDark?: boolean;

  /**
   * Whether tab is locked (permission/tier/license)
   */
  $isLocked?: boolean;

  /**
   * Whether pill (rounded) tab shapes are enabled
   */
  $pill?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTabsProps {
  $isDark: boolean;
  $glass: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
}

const StyledTabs = styled(
  Tabs,
  transientOptions,
)<StyledTabsProps>(({ theme, $isDark, $glass, $glowColor }) => {
  const colorKey = $glowColor === 'mixed' ? 'primary' : $glowColor;
  const mainColor = theme.palette[colorKey].main;

  // ── Scroll button styles ──
  // scrollButtons={true} keeps MUI's default TabScrollButton always in DOM
  // (no mount/unmount jitter). Disabled buttons collapse to 0 width so they
  // don't reserve space that causes MUI's IntersectionObserver (threshold 0.99)
  // to falsely detect overflow and enable scroll arrows when all tabs fit.
  const scrollButtonStyles = {
    '& .MuiTabs-scrollButtons': {
      width: 28,
      minWidth: 28,
      opacity: 1,
      overflow: 'hidden',
      color: $isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
      transition: 'opacity 200ms ease, color 200ms ease, width 0s',
      '&:hover': {
        color: $isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.8)',
      },
      '&.Mui-disabled': {
        opacity: 0,
        width: 0,
        minWidth: 0,
        padding: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      },
    },
  };

  return {
    minHeight: 'auto',
    borderRadius: 0,

    // ── Clean flat container — bottom border only ──
    ...($glass && {
      background: 'transparent',
      border: 'none',
      boxShadow: 'none',
      padding: 0,
    }),

    // ── Subtle baseline that the active indicator sits on top of, so
    //     unselected tabs read against a continuous low-contrast rule
    //     (matches the reference design where every tab has a faint
    //     "track" below it). ──
    boxShadow: `inset 0 -1px 0 ${alpha(theme.palette.divider, 0.6)}`,

    // ── Tab spacing — a touch wider so labels don't crowd each other ──
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(0.5),
    },

    // ── Indicator — chunkier 3px underline with a soft glow that matches
    //     the reference. Sits on top of the inset baseline above. ──
    '& .MuiTabs-indicator': {
      height: 3,
      bottom: 0,
      borderRadius: '2px 2px 0 0',
      background: mainColor,
      boxShadow: `0 0 8px ${alpha(mainColor, $isDark ? 0.5 : 0.3)}`,
    },

    ...scrollButtonStyles,
  };
});

const StyledTab = styled(
  Tab,
  transientOptions,
)<GlassTabProps>(({ theme, $size, $isDark, $isLocked, $pill }) => {
  const sizes = {
    small: {
      minHeight: 36,
      padding: theme.spacing(0.5, 1.5),
      fontSize: '0.8125rem',
    },
    medium: {
      minHeight: 44,
      padding: theme.spacing(1, 2),
      fontSize: '0.875rem',
    },
    large: {
      minHeight: 48,
      padding: theme.spacing(1.25, 2.5),
      fontSize: '0.9375rem',
    },
  };

  const sizeStyles = sizes[$size ?? 'medium'];
  const primaryMain = theme.palette.primary.main;

  return {
    ...sizeStyles,
    textTransform: 'none',
    fontWeight: 500,
    transition: transitionPresets.button,
    zIndex: 1,
    borderRadius: $pill ? 6 : 0,
    color: theme.palette.text.secondary,
    minWidth: 'auto',
    opacity: $isLocked ? 0.7 : 1,
    letterSpacing: '0.005em',
    // Comfortable spacing between the start-position icon and label.
    // Without this the icon and text touch and the row reads dense.
    gap: theme.spacing(0.875),
    '& .MuiTab-iconWrapper': {
      marginRight: 0,
      marginBottom: '0 !important',
      fontSize: '1.125rem',
      color: 'inherit',
    },

    '&:hover': {
      color: $isLocked ? theme.palette.text.secondary : theme.palette.text.primary,
      // Hover only paints a background in pill mode. Flat (underline-only)
      // mode gets a clean color-shift hover — matches the reference
      // screenshot where unselected tabs have no boxy hover treatment.
      backgroundColor: $pill && !$isLocked ? ($isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04)) : 'transparent',
      cursor: $isLocked ? 'default' : 'pointer',
      // Surface the icon at the same primary tint the indicator uses, so
      // pointing at a tab gives a small "preview of selected" cue.
      '& .MuiTab-iconWrapper': {
        color: $isLocked ? 'inherit' : primaryMain,
      },
    },

    '&.Mui-selected': {
      color: theme.palette.text.primary,
      fontWeight: 600,
      // Subtle rounded fill behind the selected tab — matches the
      // reference design's "depressed pill" treatment (the active tab
      // reads as a slight panel inset, not just a colored underline).
      // Slightly stronger than the hover bg so the selected state still
      // dominates when the user hovers a sibling tab.
      backgroundColor: $isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
      borderRadius: 6,
      '& .MuiTab-iconWrapper': {
        color: primaryMain,
      },
    },

    '&.Mui-disabled': {
      opacity: $isLocked ? 0.7 : 0.4,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

/**
 * GlassTabs
 *
 * Unified tabs component with built-in permission/tier/license locking.
 * Security features are automatically enabled when tabs include permission props.
 *
 * @example
 * ```tsx
 * // Basic tabs (no security)
 * const tabs = [
 *   { value: 'overview', label: 'Overview' },
 *   { value: 'details', label: 'Details' },
 * ];
 *
 * // Tabs with security (automatically locked when permission props present)
 * const secureTabs = [
 *   { value: 'overview', label: 'Overview' },
 *   { value: 'analytics', label: 'Analytics', requiredTier: SubscriptionTier.PROFESSIONAL },
 *   { value: 'audit', label: 'Audit Log', permission: 'audit:read' },
 *   { value: 'compliance', label: 'Compliance', requiredLicense: 'NMLS_INDIVIDUAL' },
 * ];
 *
 * <GlassTabs
 *   tabs={secureTabs}
 *   value={currentTab}
 *   onChange={(e, newValue) => setCurrentTab(newValue)}
 * />
 * ```
 */
export const GlassTabs = forwardRef<HTMLDivElement, GlassTabsProps>(
  (
    {
      tabs,
      glass = true,
      glowColor = 'primary',
      size = 'medium',
      pill = false,
      showLockIcon = true,
      showTierBadge = true,
      onLockedTabClick,
      onChange,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const permissionContext = usePermissionOptional();
    const { t } = useI18n();

    // Compute lock state for each tab (only if tab has security props)
    const tabsWithLockState = useMemo(() => {
      return tabs.map((tab) => {
        // Check if this tab has any security props
        const hasSecurityProps =
          tab.permission ||
          tab.anyPermission ||
          tab.allPermissions ||
          tab.requiredTier ||
          tab.requiredLicense ||
          tab.anyLicense;

        if (!hasSecurityProps) {
          return { ...tab, lockState: null, isLocked: false };
        }

        const lockConfig: LockConfig = {
          permission: tab.permission,
          anyPermission: tab.anyPermission,
          allPermissions: tab.allPermissions,
          requiredTier: tab.requiredTier,
          requiredLicense: tab.requiredLicense,
          anyLicense: tab.anyLicense,
        };

        const lockState = permissionContext ? permissionContext.getLockState(lockConfig) : null;

        return {
          ...tab,
          lockState,
          isLocked: !!lockState,
        };
      });
    }, [tabs, permissionContext]);

    // Handle tab change - intercept locked tab clicks
    const handleChange = useCallback(
      (event: React.SyntheticEvent, newValue: string | number) => {
        const tab = tabsWithLockState.find((t) => t.value === newValue);

        if (tab?.isLocked && tab.lockState) {
          event.preventDefault();
          event.stopPropagation();

          if (onLockedTabClick) {
            onLockedTabClick(tab, tab.lockState);
          } else if (tab.lockState.onLockedClick) {
            tab.lockState.onLockedClick();
          }
          return;
        }

        onChange?.(event, newValue);
      },
      [tabsWithLockState, onChange, onLockedTabClick],
    );

    // Render tab label with lock icon and tooltip (if locked)
    // Tooltip is placed inside the label to avoid wrapping Tab in a <span>,
    // which breaks MUI Tabs' child value detection and leaks internal props to DOM.
    const renderTabLabel = (tab: (typeof tabsWithLockState)[0]) => {
      const { label, lockState, isLocked } = tab;

      if (!isLocked || !showLockIcon || !lockState) {
        return label;
      }

      const lockLabel = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{label}</span>
          <LockIcon
            sx={{
              fontSize: iconSizes.xs,
              color:
                lockState.type === 'tier'
                  ? theme.palette.warning.main
                  : lockState.type === 'license'
                    ? theme.palette.info.main
                    : theme.palette.action.disabled,
            }}
          />
          {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
            <GlassChip
              label={getTierLabel(lockState.requiredTier)}
              size="small"
              color="warning"
              sx={{
                height: 18,
                fontSize: '0.75rem',
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          )}
        </Box>
      );

      return (
        <GlassTooltip title={lockState.reason ?? t('secure.noAccess')} placement="top">
          {lockLabel}
        </GlassTooltip>
      );
    };

    // Render individual tab — always a direct StyledTab child of Tabs.
    // Locked tabs stay enabled so Tooltip shows and handleChange can intercept clicks.
    // Only explicitly-disabled tabs use the HTML disabled attribute.
    const renderTab = (tab: (typeof tabsWithLockState)[0]) => {
      const { value, icon, disabled, isLocked } = tab;

      return (
        <StyledTab
          key={value}
          label={renderTabLabel(tab)}
          value={value}
          icon={icon}
          disabled={!!disabled}
          aria-disabled={isLocked || disabled || undefined}
          data-testid={`tab-${String(value).toLowerCase()}`}
          $size={size}
          $isDark={isDark}
          $isLocked={isLocked}
          $pill={pill}
          iconPosition="start"
        />
      );
    };

    return (
      <Box ref={ref} sx={{ maxWidth: '100%' }}>
        <StyledTabs
          $isDark={isDark}
          $glass={glass}
          $glowColor={glowColor}
          variant="scrollable"
          scrollButtons={true}
          onChange={handleChange}
          {...props}
        >
          {tabsWithLockState.map(renderTab)}
        </StyledTabs>
      </Box>
    );
  },
);

GlassTabs.displayName = 'GlassTabs';

export default GlassTabs;
